import app from '../app';
import Component from '../../common/Component';
import m from 'mithril';
import type Mithril from 'mithril';
import LogInModal from './LogInModal';
import Button from '../../common/components/Button';
interface CurrentOpenimChat {
  chatRoomGroupId: string;
  userFeedbackGroupId: string;
  goodLuckGroupId: string;
  groupAdminId: string;
}

export interface IChatRoom {
  channel: string;
  channelId?: string;
  channelType?: number;
  className?: string;
  onclick?: (e: MouseEvent) => void;
  children?: Mithril.ChildArray;
  icon?: string;
  }

/**
 * ChatRoom 组件 - 专注于聊天界面的打开/关闭逻辑
 * 外观和形式由父组件通过 children 决定
 */
export default class ChatRoom extends Component<IChatRoom> {
  // 共享当前打开的 iframe 状态，避免重复打开
  static currentIframe?: HTMLIFrameElement;
  static currentCloseBtn?: HTMLButtonElement;
  static currentChannelId?: string;
  
  // 未读数数据缓存
  static unreadCountMap: Record<string, { atNum: number; isUnread: number }> = {};
  static refreshTimer?: number;
  static fetchInFlight?: Promise<void>;
  static lastFetchAt = 0;
  static listenersBound = false;
  static onFocusHandler?: () => void;
  static onVisibilityHandler?: () => void;
  static onOnlineHandler?: () => void;

  static initAttrs(attrs: IChatRoom) {
    const currentOpenimChat = (app.data as any).currentOpenimChat as CurrentOpenimChat | undefined;
    
    // 根据 channel 设置 channelId 和 channelType
    switch (attrs.channel) {
      case '1':
        attrs.channelId = currentOpenimChat?.chatRoomGroupId || '';
        attrs.channelType = 3;
        break;
      case '2':
        attrs.channelId = currentOpenimChat?.userFeedbackGroupId || '';
        attrs.channelType = 3;
        break;
      case '3':
        attrs.channelId = currentOpenimChat?.goodLuckGroupId || '';
        attrs.channelType = 3;
        break;
      case '4':
        attrs.channelId = currentOpenimChat?.groupAdminId || '';
        attrs.channelType = 1;
        break;
      default:
        break;
    }
  }

  view(vnode: Mithril.Vnode<IChatRoom, this>) {
    const { className, onclick, icon } = this.attrs;
    // 从 vnode 中获取 children
    const children = (vnode?.children as Mithril.ChildArray) || this.attrs.children;
    const clickHandler = onclick || this.handleClick.bind(this);
    
    // 如果父组件传递了自定义的 onclick，优先使用
    // 否则使用 ChatRoom 的默认点击处理
    
    // 如果没有 children，返回空（父组件必须传递内容）
    if (!children || (Array.isArray(children) && children.length === 0)) {
      return null;
    }
    
    // 始终返回 button 元素，按钮的外观由父组件通过 className 和 children 控制
    return (
      <Button icon={icon} className={className} onclick={clickHandler} type="button">
        {children}
      </Button>
    );
  }

  handleClick(e: MouseEvent) {
    e.stopPropagation();
    
    if (!app.drawer.isOpen()) {
      app.drawer.show();
    }
    
    ChatRoom.openChat({
      channelId: this.attrs.channelId || '',
      channel: this.attrs.channel,
      channelType: this.attrs.channelType || 3,
    });
  }

  /**
   * 打开聊天界面的静态方法，供外部调用
   * @param options 聊天配置选项
   */
  static openChat(options: { channelId: string; channel: string; channelType: number }) {
    if (!app.session.user) {
      app.modal.show(LogInModal);
      return;
    }

    const { channelId: requestedChannelId, channel, channelType } = options;

    // 如果同一个频道已打开，则不重复处理
    if (ChatRoom.currentIframe && ChatRoom.currentChannelId === requestedChannelId) {
      return;
    }

    // 如果已打开的是其他频道，先关闭旧 iframe
    ChatRoom.closeChat();

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/chatUserLogin',
        params: {
          group: requestedChannelId,
          type: channelType == 1 ? 2 : 1, // 1:群组 2:私聊
        },
      })
      .then((res: any) => {
        if (res) {
          let userToken = res.token;
          const baseConfig = {
            userID: app.session.user?.uniqueID().toString(), // IM 用户 userID
            token: userToken, // IM 用户令牌
            channelId: requestedChannelId,
            channel: channel,
            channelType: channelType,
            pkgId: String(((app.data as any).pkgLogo?.id) ?? ''),
            imgUrl: `${((app.data as any).pkgLogo?.cdnUrl) || ''}/${((app.data as any).pkgLogo?.ossFile) || ''}`,
          };

          // 获取白名单并放入 config 数组字段一并传递
          app
            .request({
              method: 'POST',
              url: app.forum.attribute('apiUrl') + '/chatGetWhiteIp',
            })
            .then((whiteRes: any) => {
              const ip = whiteRes?.whiteIp?.ip;
              const config = {
                ...baseConfig,
                whiteList: ip ? ip.split(',') : [],
                locale: app.data.locale,
              };
              console.log('OpenIM config:', config);

              const url: string = app.forum.attribute('imUrl');
              const iframe = document.createElement('iframe');
              iframe.src = url;
              iframe.classList.add('open-im');
              iframe.title = 'Chat Window'; // 必填！保证可访问性
              iframe.frameBorder = '0'; // 移除边框
              iframe.sandbox = 'allow-same-origin allow-scripts allow-forms allow-popups allow-popups-to-escape-sandbox allow-modals'; // 安全限制
              iframe.addEventListener('load', () => {
                // 需等待iframe加载完成
                if (iframe.contentWindow) {
                  iframe.contentWindow.postMessage(
                    JSON.stringify(config),
                    url // 指定子页面源，提升安全性
                  );
                }
              });
              
              // 创建关闭按钮
              const closeBtn = document.createElement('button');
              closeBtn.className = 'close-chat-btn';
              closeBtn.onclick = () => {
                ChatRoom.closeChat();
              };
              
              document.documentElement.classList.add('modal-open');
              document.body.appendChild(iframe);
              document.body.appendChild(closeBtn);
              ChatRoom.currentIframe = iframe;
              ChatRoom.currentCloseBtn = closeBtn;
              ChatRoom.currentChannelId = requestedChannelId;

              // 弹窗出现后立即触发重渲染，让入口按钮等立刻切换到“关闭”态
              m.redraw();
              
              // 打开聊天后刷新未读数
              ChatRoom.requestUnreadRefresh(true);
            });
        }
      });
  }

  /**
   * 关闭聊天界面的静态方法
   */
  static closeChat() {
    if (ChatRoom.currentIframe) {
      document.body.removeChild(ChatRoom.currentIframe);
      ChatRoom.currentIframe = undefined;
    }
    if (ChatRoom.currentCloseBtn) {
      document.body.removeChild(ChatRoom.currentCloseBtn);
      ChatRoom.currentCloseBtn = undefined;
    }
    document.documentElement.classList.remove('modal-open');
    ChatRoom.currentChannelId = undefined;
    // 关闭聊天后刷新界面，以便入口按钮等联动更新
    m.redraw();
    // 关闭聊天后也刷新一次未读数，保证红点消除更及时
    ChatRoom.requestUnreadRefresh(true);
  }

  /**
   * 检查指定频道是否已打开
   */
  static isChatOpen(channelId?: string): boolean {
    if (!channelId) {
      return !!ChatRoom.currentIframe;
    }
    return ChatRoom.currentChannelId === channelId;
  }

  /**
   * 获取未读数数量
   * @param channelId 频道ID
   * @returns 未读数信息 { atNum: number, isUnread: number }
   */
  static getUnreadCount(channelId: string,type: number = 1): { atNum: number; isUnread: number } | null {
    if (!channelId) return null;
    const id = type === 1 ? 'sg_' + channelId : 'si_' + channelId + '_' + app.session.user?.uniqueID().toString();
    // 直接使用 channelId 从缓存中获取
    return ChatRoom.unreadCountMap[id] || null;
  }

  /**
   * 获取所有未读数数据
   */
  static fetchUnreadCounts(): Promise<void> {
    if (!app.session.user) {
      return Promise.resolve();
    }

    return app
      .request({
        method: 'GET',
        url: app.forum.attribute('apiUrl') + '/chatGetUserAtNumIsUnread',
      })
      .then((res: any) => {
        if (res && res.atNumListMap) {
          ChatRoom.unreadCountMap = res.atNumListMap;
          // 触发重新渲染
          m.redraw();
        } else {
          ChatRoom.unreadCountMap = {};
        }
      })
      .catch((e) => {
        console.error('Failed to fetch unread counts:', e);
        ChatRoom.unreadCountMap = {};
      });
  }

  /**
   * 请求刷新未读数（带节流/防并发）
   * @param force 是否强制刷新（忽略节流窗口）
   */
  static requestUnreadRefresh(force = false): Promise<void> {
    if (!app.session.user) return Promise.resolve();

    // 防并发：如果已有请求在飞，直接复用
    if (ChatRoom.fetchInFlight) return ChatRoom.fetchInFlight;

    const now = Date.now();
    // 节流：默认 5 秒内最多刷新一次，避免频繁触发导致接口压力
    if (!force && now - ChatRoom.lastFetchAt < 5000) {
      return Promise.resolve();
    }

    ChatRoom.lastFetchAt = now;
    ChatRoom.fetchInFlight = ChatRoom.fetchUnreadCounts().finally(() => {
      ChatRoom.fetchInFlight = undefined;
    });
    return ChatRoom.fetchInFlight;
  }

  /**
   * 开始未读数刷新（仅条件触发，不做定时轮询），并在窗口恢复焦点/从后台切回时立即刷新
   */
  static startUnreadCountRefresh(): void {
    // 立即获取一次（强制）
    ChatRoom.requestUnreadRefresh(true);

    // 绑定一次全局监听：切回页面/窗口时更及时刷新红点
    if (!ChatRoom.listenersBound) {
      ChatRoom.listenersBound = true;

      ChatRoom.onFocusHandler = () => {
        ChatRoom.requestUnreadRefresh(true);
      };
      ChatRoom.onVisibilityHandler = () => {
        if (document.visibilityState === 'visible') {
          ChatRoom.requestUnreadRefresh(true);
        }
      };
      ChatRoom.onOnlineHandler = () => {
        ChatRoom.requestUnreadRefresh(true);
      };

      window.addEventListener('focus', ChatRoom.onFocusHandler);
      document.addEventListener('visibilitychange', ChatRoom.onVisibilityHandler);
      window.addEventListener('online', ChatRoom.onOnlineHandler);
    }
  }

  /**
   * 停止定期刷新未读数
   */
  static stopUnreadCountRefresh(): void {
    if (ChatRoom.refreshTimer) {
      clearInterval(ChatRoom.refreshTimer);
      ChatRoom.refreshTimer = undefined;
    }
  }
}
